import { BODY_PART_OPTIONS, EQUIPMENT_OPTIONS } from '../select-options';

import type { QueryParams } from '../../types/api/query.types';
import type { Exercise } from '../../types/domain/exercise.types';
import type { TemplateExerciseOutput } from '../../validation/template.schemas';

/**
 * Exercise Picker Model
 *
 * "Add Exercise" in a workout or plan form: search the library, narrow it by
 * body part and equipment, tick exercises, and add them. The rules are the same
 * on web (`AddExerciseModal`) and mobile (`ExercisePicker`) and live here as
 * pure functions; each platform draws it and loads the pages.
 */

// =============================================================================
// FILTERS
// =============================================================================

export interface ExercisePickerFilterOption {
  value: string;
  /** In capitals, as the filter chips show it. */
  label: string;
}

const toFilterOptions = (
  options: readonly { value: string; label: string }[]
): ExercisePickerFilterOption[] =>
  options.map((option) => ({
    value: option.value,
    label: option.label.toUpperCase(),
  }));

export const EXERCISE_PICKER_BODY_PART_FILTERS =
  toFilterOptions(BODY_PART_OPTIONS);

export const EXERCISE_PICKER_EQUIPMENT_FILTERS =
  toFilterOptions(EQUIPMENT_OPTIONS);

export interface ExercisePickerFilters {
  search: string;
  bodyParts: string[];
  equipment: string[];
}

export const EMPTY_EXERCISE_PICKER_FILTERS: ExercisePickerFilters = {
  search: '',
  bodyParts: [],
  equipment: [],
};

/**
 * How long the search waits after the last keystroke before it asks — the
 * same pause as the global search. Body part and equipment apply at once.
 */
export const EXERCISE_PICKER_SEARCH_DEBOUNCE_MS = 300;

/**
 * The search text to ask with, given what is typed and its debounced copy.
 * Typing waits for the pause; emptying the field does not, so the clear button
 * brings the whole library back at once.
 */
export function getEffectiveSearch(typed: string, debounced: string): string {
  return typed === '' ? '' : debounced;
}

/** Whether a body-part or equipment filter is on. The search does not count. */
export function hasExercisePickerFilters(
  filters: ExercisePickerFilters
): boolean {
  return filters.bodyParts.length > 0 || filters.equipment.length > 0;
}

/** The query the exercise list is asked with; each filter is comma-joined. */
export function buildExercisePickerQuery(
  filters: ExercisePickerFilters
): QueryParams {
  return {
    search: filters.search,
    body_part:
      filters.bodyParts.length > 0 ? filters.bodyParts.join(',') : undefined,
    equipment:
      filters.equipment.length > 0 ? filters.equipment.join(',') : undefined,
  };
}

// =============================================================================
// PICKING
// =============================================================================

/**
 * The picked exercises with one ticked or unticked, in the order they were
 * ticked. The exercise is kept with its id because a picked exercise can leave
 * the list — by a new search — before it is added.
 */
export function togglePickedExercise(
  picked: ReadonlyMap<string, Exercise>,
  exercise: Exercise
): Map<string, Exercise> {
  const next = new Map(picked);
  if (next.has(exercise.id)) {
    next.delete(exercise.id);
  } else {
    next.set(exercise.id, exercise);
  }
  return next;
}

/**
 * The picked exercises as a form holds them: each a new row with no sets,
 * outside any group. `createId` supplies the row's uuid.
 */
export function toTemplateExercises(
  picked: Iterable<Exercise>,
  createId: () => string
): TemplateExerciseOutput[] {
  return Array.from(picked, (exercise) => ({
    id: null,
    uuid: createId(),
    guid: exercise.id,
    name: exercise.name,
    tracking_type: exercise.trackingType ?? 'weight_reps',
    group_leader: null,
    sets: [],
  }));
}

// =============================================================================
// LABELS
// =============================================================================

/** "14 matches" / "1 match". */
export function getMatchCountLabel(total: number): string {
  return `${total} ${total === 1 ? 'match' : 'matches'}`;
}

/** "3 exercises added" / "1 exercise added". */
export function getAddedCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'exercise' : 'exercises'} added`;
}
